/**
 * registry-import.js — CUR-145. "Import a registry" as ONE flow, shared by:
 *   • the registry-public page's "Import a Registry" buttons
 *     ([data-registry-import-cta]): a popup asks for the link, previews the
 *     items, adds the ticked ones — creating the registry first if there isn't
 *     one, guests included — then opens the editor on it;
 *   • the editor's "Copy a registry" bar (registry-unified.js startCopyImport),
 *     which already has the link and starts at the preview.
 *
 * The preview/commit steps moved here verbatim from registry-unified.js
 * (renderImportPreview / commitImport / renderImportError), so both surfaces
 * run the same code against POST /apps/registry/api/import/{preview,commit}.
 *
 * Needs window.BirchRegistry (registry-store.js) and the .reg-import-* /
 * .registry-modal styles in registry.css. On a page that does not load
 * registry.css, pass its URL as data-registry-css on the button and it is
 * fetched on first click. Without BirchRegistry the button stays a plain link
 * to the editor's copy box (/pages/registry-edit?import=1).
 *
 * Exposes window.BirchRegistryImport.
 */
(function () {
  'use strict';

  function BR() { return window.BirchRegistry; }

  function errorMessage(code) {
    if (code === 'birch_registry_not_found') return 'We couldn’t find that Birch registry. Double-check the link.';
    // CUR-178 — the server won't list a private registry's items for anyone but its owner.
    if (code === 'birch_registry_private') return 'That registry is private, so its items can’t be imported.';
    if (code === 'invalid_url' || code === 'birch_slug_missing') return 'That doesn’t look like a registry link we can read.';
    if (code === 'no_items_found') return 'We couldn’t read any items from that link. It may be private, empty, or not supported yet.';
    return 'Something went wrong reading that registry. Please try again.';
  }

  function importableCount(items) {
    return (items || []).filter(function (it) { return !it.alreadyInRegistry; }).length;
  }

  /** The add payloads for the ticked rows. An item already on the registry is
   *  never sent, whatever the checkbox says. */
  function selectedAdds(items, keys) {
    var picked = {};
    (keys || []).forEach(function (k) { picked[k] = true; });
    return (items || [])
      .filter(function (it) { return picked[it.key] && !it.alreadyInRegistry; })
      .map(function (it) { return it.add; });
  }

  /** Where the public page sends the parent once the items are added. */
  function doneUrl(registryId, added) {
    return '/pages/registry-edit?id=' + encodeURIComponent(registryId) + '&imported=' + (parseInt(added, 10) || 0);
  }

  // ── modal plumbing ────────────────────────────────────────────────────────
  function openModalShell() {
    var backdrop = document.createElement('div');
    backdrop.className = 'registry-modal-backdrop';
    backdrop.innerHTML = '<div class="registry-modal reg-import-modal" role="dialog" aria-modal="true" aria-label="Import any registry"></div>';
    document.body.appendChild(backdrop);
    var modal = backdrop.querySelector('.reg-import-modal');
    var onKey = function (e) { if (e.key === 'Escape') close(); };
    var close = function () { backdrop.remove(); document.removeEventListener('keydown', onKey); };
    backdrop.addEventListener('click', function (e) { if (e.target === backdrop) close(); });
    document.addEventListener('keydown', onKey);
    return { modal: modal, close: close };
  }

  function renderReading(modal) {
    modal.innerHTML =
      '<div style="text-align:center;padding:24px 8px">' +
        '<div style="display:flex;justify-content:center;margin-bottom:14px"><span class="registry-loading" style="width:28px;height:28px;border-width:3px"></span></div>' +
        '<h2 style="font-size:1.0625rem;margin-bottom:6px">Reading that registry&hellip;</h2>' +
        '<p style="color:var(--reg-muted);font-size:0.875rem;margin:0">Fetching items and matching them to Birch. This can take a few seconds.</p>' +
      '</div>';
  }

  // ── step 1 (popup only): the link ─────────────────────────────────────────
  function renderLinkStep(shell, opts) {
    var modal = shell.modal;
    modal.innerHTML =
      '<div class="reg-import-modal__head">' +
        '<h2 style="margin:0">Import any Registry</h2>' +
        // CUR-166 — the sources, up front (all six are recognised by birch-registry
        // importSource.js), replacing the small note that sat under the input.
        '<p class="reg-import-modal__sub">Import from other registries including Babylist, Zola, Poppylist, Amazon, MyRegistry, or another Birch registry.</p>' +
      '</div>' +
      '<div class="registry-modal__field">' +
        '<label for="reg-import-url">Registry link</label>' +
        '<input id="reg-import-url" type="url" inputmode="url" autocomplete="off" placeholder="Paste a registry link" data-import-url>' +
      '</div>' +
      '<div class="registry-modal__actions">' +
        '<button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Cancel</button>' +
        '<button type="button" class="registry-btn registry-btn--primary" data-action="find">Find items</button>' +
      '</div>';
    var input = modal.querySelector('[data-import-url]');
    var go = function () {
      preview(input.value, Object.assign({}, opts, { onBack: function () { renderLinkStep(shell, opts); } }), shell);
    };
    modal.querySelector('[data-action="find"]').addEventListener('click', go);
    modal.querySelector('[data-action="cancel"]').addEventListener('click', shell.close);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); go(); } });
    setTimeout(function () { try { input.focus(); } catch (_) { /* not focusable yet */ } }, 50);
  }

  // ── step 2: read the registry ─────────────────────────────────────────────
  /**
   * opts: {
   *   registryId,                     // for "already on your registry" flags; may be null
   *   getRegistryId: () => Promise<id>, // resolves (or creates) the registry to add into
   *   commitLabel: () => string,       // the add button's words
   *   onImported({ added, skipped, registryId, sourceName }),
   *   onBack?: () => void,            // "Try another link" (popup only)
   * }
   */
  function preview(rawUrl, opts, shell) {
    var br = BR();
    var url = String(rawUrl || '').trim();
    if (!url) { br.showToast('Paste a registry link first'); return Promise.resolve(); }
    shell = shell || openModalShell();
    renderReading(shell.modal);
    return Promise.resolve(br.API.importPreview(url, opts.registryId || null)).then(function (res) {
      if (!res || !Array.isArray(res.items) || !res.items.length) {
        renderError(shell, (res && res.error) || 'no_items_found', opts);
        return;
      }
      renderPreview(shell, res, opts);
    }, function (e) {
      renderError(shell, (e && e.body && e.body.error) || (e && e.message), opts);
    });
  }

  // ── step 3: choose, then add ──────────────────────────────────────────────
  function renderPreview(shell, res, opts) {
    var br = BR();
    var escapeHtml = br.escapeHtml;
    var formatMoney = br.formatMoney;
    var modal = shell.modal;
    var items = res.items;
    var dupCount = items.filter(function (it) { return it.alreadyInRegistry; }).length;
    var importable = items.length - dupCount;
    var label = opts.commitLabel ? opts.commitLabel() : 'Add to my registry';
    var rows = items.map(function (it) {
      var locked = !!it.alreadyInRegistry; // already on the registry → skip
      var badge = it.matchType === 'birch'
        ? '<span class="reg-import-badge reg-import-badge--birch">Birch' + (it.matchedFrom ? ' match' : '') + '</span>'
        : '<span class="reg-import-badge reg-import-badge--external">' + escapeHtml(it.retailer || 'External') + '</span>';
      var statusBadge = '';
      if (locked) statusBadge = '<span class="reg-import-badge reg-import-badge--added">Already added</span>';
      else if (it.unavailable) statusBadge = '<span class="reg-import-badge reg-import-badge--oos">No longer available</span>';
      else if (it.matchType === 'birch' && it.inStock === false) statusBadge = '<span class="reg-import-badge reg-import-badge--oos">Out of stock</span>';
      var price = it.priceCents != null ? '<span>' + formatMoney(it.priceCents, it.currency || 'USD') + '</span>' : '';
      var img = it.imageUrl
        ? '<img class="reg-import-row__img" src="' + escapeHtml(it.imageUrl) + '" alt="" loading="lazy" onerror="this.style.visibility=\'hidden\'">'
        : '<div class="reg-import-row__img"></div>';
      return '<li class="reg-import-row' + (locked ? ' reg-import-row--off reg-import-row--locked' : '') + '" data-key="' + escapeHtml(it.key) + '">' +
          '<input type="checkbox" class="reg-import-row__check" ' + (locked ? '' : 'checked') + ' ' + (locked ? 'disabled' : '') + ' aria-label="Import ' + escapeHtml(it.title) + '">' +
          img +
          '<div class="reg-import-row__body">' +
            '<div class="reg-import-row__title">' + escapeHtml(it.title) + '</div>' +
            '<div class="reg-import-row__meta">' + badge + statusBadge + price + '</div>' +
          '</div>' +
        '</li>';
    }).join('');

    var dupNote = dupCount
      ? '<p class="reg-import-note">' + dupCount + ' item' + (dupCount === 1 ? '' : 's') + ' already on your registry ' + (dupCount === 1 ? 'is' : 'are') + ' marked and will be skipped.</p>'
      : '';

    modal.innerHTML =
      '<div class="reg-import-modal__head">' +
        '<h2 style="margin:0">Import ' + importable + ' item' + (importable === 1 ? '' : 's') + '</h2>' +
        '<p class="reg-import-modal__sub">From ' + escapeHtml(res.sourceName || 'your registry') + '. Everything’s ticked — untick anything you don’t want.' + (res.truncated ? ' Showing the first ' + items.length + '.' : '') + '</p>' +
        dupNote +
      '</div>' +
      '<div class="reg-import-toolbar">' +
        '<span data-count></span>' +
        '<span><button type="button" data-action="all">Select all</button> &middot; <button type="button" data-action="none">Clear all</button></span>' +
      '</div>' +
      '<ul class="reg-import-list">' + rows + '</ul>' +
      '<div class="registry-modal__actions">' +
        '<button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Cancel</button>' +
        '<button type="button" class="registry-btn registry-btn--primary" data-action="import">' + escapeHtml(label) + '</button>' +
      '</div>';

    var list = modal.querySelector('.reg-import-list');
    var countEl = modal.querySelector('[data-count]');
    var importBtn = modal.querySelector('[data-action="import"]');
    var updateCount = function () {
      var n = list.querySelectorAll('.reg-import-row__check:checked').length;
      countEl.textContent = n + ' of ' + importable + ' selected';
      importBtn.disabled = n === 0;
    };
    list.addEventListener('change', function (e) {
      if (!e.target.classList.contains('reg-import-row__check')) return;
      e.target.closest('.reg-import-row').classList.toggle('reg-import-row--off', !e.target.checked);
      updateCount();
    });
    // Select-all / clear-all only touch importable (non-disabled) rows.
    var setAll = function (checked) {
      list.querySelectorAll('.reg-import-row__check:not([disabled])').forEach(function (c) {
        c.checked = checked;
        c.closest('.reg-import-row').classList.toggle('reg-import-row--off', !checked);
      });
      updateCount();
    };
    modal.querySelector('[data-action="all"]').addEventListener('click', function () { setAll(true); });
    modal.querySelector('[data-action="none"]').addEventListener('click', function () { setAll(false); });
    modal.querySelector('[data-action="cancel"]').addEventListener('click', shell.close);
    importBtn.addEventListener('click', function () { commit(shell, res, opts, label); });
    updateCount();
  }

  function commit(shell, res, opts, label) {
    var br = BR();
    var modal = shell.modal;
    var keys = Array.prototype.map.call(
      modal.querySelectorAll('.reg-import-row__check:checked'),
      function (c) { return c.closest('.reg-import-row').dataset.key; });
    var selected = selectedAdds(res.items, keys);
    if (!selected.length) { br.showToast('Pick at least one item'); return; }
    var importBtn = modal.querySelector('[data-action="import"]');
    importBtn.disabled = true;
    importBtn.textContent = 'Adding…';
    var registryId = null;
    Promise.resolve(opts.getRegistryId()).then(function (id) {
      registryId = id;
      return br.API.importCommit({ registryId: id, items: selected, sourceName: res.sourceName || null });
    }).then(function (result) {
      shell.close();
      if (opts.onImported) {
        opts.onImported({
          added: (result && result.added) || 0,
          skipped: (result && result.skipped) || 0,
          registryId: registryId,
          sourceName: res.sourceName || null,
        });
      }
    }, function (e) {
      importBtn.disabled = false;
      importBtn.textContent = label;
      br.showToast((e && e.message) || 'Could not import items');
    });
  }

  function renderError(shell, code, opts) {
    var escapeHtml = BR().escapeHtml;
    var modal = shell.modal;
    modal.innerHTML =
      '<div class="reg-import-empty">' +
        '<p style="margin:0 0 16px">' + escapeHtml(errorMessage(code)) + '</p>' +
        '<div class="registry-modal__actions" style="justify-content:center">' +
          (opts && opts.onBack ? '<button type="button" class="registry-btn registry-btn--primary" data-action="back">Try another link</button>' : '') +
          '<button type="button" class="registry-btn registry-btn--secondary" data-action="cancel">Close</button>' +
        '</div>' +
      '</div>';
    modal.querySelector('[data-action="cancel"]').addEventListener('click', shell.close);
    var back = modal.querySelector('[data-action="back"]');
    if (back) back.addEventListener('click', opts.onBack);
  }

  /** The popup, from the link step. */
  function open(opts) {
    renderLinkStep(openModalShell(), opts || {});
  }

  // ── the registry-public page's buttons ────────────────────────────────────
  // registry.css (the modal and .reg-import-* styles) is not on that page;
  // fetch it on first use rather than on every visit to a marketing page.
  function ensureCss(href) {
    if (!href || document.querySelector('link[data-registry-import-css]')) return Promise.resolve();
    return new Promise(function (resolve) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.setAttribute('data-registry-import-css', '');
      link.onload = link.onerror = function () { resolve(); };
      document.head.appendChild(link);
      setTimeout(resolve, 3000); // never leave the click hanging on a slow CDN
    });
  }

  document.addEventListener('click', function (e) {
    var cta = e.target && e.target.closest && e.target.closest('[data-registry-import-cta]');
    if (!cta) return;
    var br = BR();
    // No registry SDK on this page: the href (the editor's copy box) still works.
    if (!br || !br.API || !br.getOrCreateRegistry) return;
    e.preventDefault();
    ensureCss(cta.getAttribute('data-registry-css')).then(function () {
      open({
        registryId: null,
        getRegistryId: function () {
          return br.getOrCreateRegistry({
            registrantFirst: cta.getAttribute('data-customer-first') || '',
            registrantLast: cta.getAttribute('data-customer-last') || '',
            customerEmail: cta.getAttribute('data-customer-email') || '',
          }).then(function (r) { return r.registry.id; });
        },
        commitLabel: function () { return 'Add to my registry'; },
        onImported: function (r) { location.href = doneUrl(r.registryId, r.added); },
      });
    });
  });

  window.BirchRegistryImport = {
    open: open,
    preview: preview,
    errorMessage: errorMessage,
    importableCount: importableCount,
    selectedAdds: selectedAdds,
    doneUrl: doneUrl,
  };
})();
